"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const lodash_1 = __importDefault(require("lodash"));
const react_1 = require("react");
const FormContexts_1 = require("../Contexts/FormContexts");
const mapHelpers_1 = require("../utils/mapHelpers");
const ConditionalChildren_1 = require("./ConditionalChildren");
const V2FormTemplate_1 = require("../types/V2FormTemplate");
/**
 *  This component will build the validation function used to validate a form control's value. This function is then passed on to be used by the final-form field for rendering.
 * ### Note ###
 * This component requires the evaluation of the form values whether or not the validation is specified or used, so it is highly recommended that the ValidationOptimizer be used
 * instead of this Component directly.
 * @param controlDefinition The definition of this control. This is where the definitions for the various validation maps can be found
 * @param render The render prop used to render the control. This component provides the render prop with the validation function if one is appropriate, or undefined if not
 */
const ValidationRender = (props) => {
    const { controlDefinition: { options, warningMap, errorMap, required, label, type }, render, } = props;
    const optionsMap = options && options.type === V2FormTemplate_1.DynamicLoaderType && options.optionsMap;
    const { min, max } = props.controlDefinition;
    const formDataMapping = (0, FormContexts_1.useFormDataMappingSources)(); // get any supplemental state to pass to data mapper
    const { functionDefinitions } = (0, FormContexts_1.useControlProvider)();
    const [prevErr, setPrevErr] = (0, react_1.useState)([]);
    const [prevWarn, setPrevWarn] = (0, react_1.useState)([]);
    const [prevOptions, setPrevOptions] = (0, react_1.useState)();
    const errorRefs = (0, react_1.useMemo)(() => {
        if (errorMap) {
            let result = {};
            lodash_1.default.each(errorMap, (map) => {
                const newRefs = (0, ConditionalChildren_1.getRefs)(map, ['FORM', 'FORMVALUE']);
                if (Object.keys(newRefs).length > 0) {
                    result = lodash_1.default.merge(result, newRefs);
                }
            });
            return result;
        }
        else {
            return {};
        }
    }, [errorMap]);
    const warningRefs = (0, react_1.useMemo)(() => {
        if (warningMap) {
            let result = {};
            lodash_1.default.each(warningMap, (map) => {
                const newRefs = (0, ConditionalChildren_1.getRefs)(map, ['FORM', 'FORMVALUE']);
                if (Object.keys(newRefs).length > 0) {
                    result = lodash_1.default.merge(result, newRefs);
                }
            });
            return result;
        }
        else {
            return {};
        }
    }, [warningMap]);
    const optionRefs = (0, react_1.useMemo)(() => {
        if (optionsMap) {
            return (0, ConditionalChildren_1.getRefs)(optionsMap, ['FORM', 'FORMVALUE']);
        }
        else {
            return {};
        }
    }, [optionsMap]);
    const [firstErrorMap, setFirstErrorMap] = (0, react_1.useState)(false);
    const [firstWarningMap, setFirstWarningMap] = (0, react_1.useState)(false);
    const [firstOptionsMap, setFirstOptionsMap] = (0, react_1.useState)(false);
    const [previousState, setPreviousState] = (0, react_1.useState)({});
    const [previousValue, setPreviousValue] = (0, react_1.useState)({});
    // we're going to create a validation function that returns errors and warnings
    const validationFn = (0, react_1.useCallback)(async (value, allValues, _ignoreTouched, path) => {
        var _a;
        // now we need to build a set of data sources for our datamap
        let errors = [];
        let warnings = [];
        let options;
        //only check if the field has been interacted with, plus allow override for modal validation
        const parts = (path || '').split('.');
        const t = parts.map((part, idx) => {
            let target = allValues;
            let name = '';
            for (let i = 0; i < idx; i++) {
                target = lodash_1.default.get(target, parts[i]);
                name += parts[i].replace(/\[\d+\]/, '') + '.';
            }
            target = target ? lodash_1.default.get(target, part) : undefined;
            name += part.replace(/\[\d+\]/, '');
            return { [name]: target };
        });
        let formValue = { value };
        for (let i = 0; i < t.length; i++) {
            formValue = Object.assign(Object.assign({}, formValue), t[i]);
        }
        // only
        if (errorMap || warningMap || optionsMap) {
            const accessors = Object.assign(Object.assign({}, formDataMapping.accessors), { FORM: () => {
                    return allValues;
                }, FORMVALUE: () => formValue });
            if (errorMap &&
                (!firstErrorMap ||
                    (errorRefs &&
                        ((0, ConditionalChildren_1.anyValuesChanged)(errorRefs['FORMVALUE'], previousValue, formValue) ||
                            (0, ConditionalChildren_1.anyValuesChanged)(errorRefs['FORM'], previousState, allValues))))) {
                const foundErrors = await (0, mapHelpers_1.fieldErrorMapEvaluator)(accessors, functionDefinitions, errorMap);
                errors.push(...foundErrors);
                setPrevErr(errors);
            }
            else {
                //If we don't need to recalculate, get all previous errors except "is required" error, which is calculated below.
                errors = prevErr.filter((val) => !required || val !== `${label} is required`);
            }
            if (warningMap &&
                (!firstWarningMap ||
                    (warningRefs &&
                        ((0, ConditionalChildren_1.anyValuesChanged)(warningRefs['FORMVALUE'], previousValue, formValue) ||
                            (0, ConditionalChildren_1.anyValuesChanged)(warningRefs['FORM'], previousState, allValues))))) {
                const foundWarnings = await (0, mapHelpers_1.fieldWarningMapEvaluator)(accessors, functionDefinitions, warningMap);
                warnings.push(...foundWarnings);
                setPrevWarn(warnings);
            }
            else {
                warnings = prevWarn;
            }
            if (optionsMap &&
                (!firstOptionsMap ||
                    (optionRefs &&
                        ((0, ConditionalChildren_1.anyValuesChanged)(optionRefs['FORMVALUE'], previousValue, formValue) ||
                            (0, ConditionalChildren_1.anyValuesChanged)(optionRefs['FORM'], previousState, allValues))))) {
                options = (_a = (await (0, mapHelpers_1.fieldOptionsMapEvaluator)(accessors, functionDefinitions, optionsMap))) === null || _a === void 0 ? void 0 : _a.filter(opt => opt);
                setPrevOptions(options);
            }
            else {
                options = prevOptions;
            }
            setPreviousState(lodash_1.default.cloneDeep(allValues));
            setPreviousValue(lodash_1.default.cloneDeep(formValue));
            setFirstWarningMap(true);
            setFirstErrorMap(true);
            setFirstOptionsMap(true);
        }
        // TODO: Update Form Renderer with internationalization
        const hasLocation = (val) => Array.isArray(val) ? (val.length && val.every(v => v === null || v === void 0 ? void 0 : v.location)) : val === null || val === void 0 ? void 0 : val.location;
        if (required && (!value || type === 'location' && !hasLocation(value))) {
            errors.push(`${label} is required`);
        }
        if (!(min && max && min > 0 && max > 0 && min > max)) {
            if (min && min > 0 && Array.isArray(value) && value.length < min) {
                errors.push(`Number of files selected does not meet the minimum of ${min}`);
            }
            if (max && max > 0 && Array.isArray(value) && value.length > max) {
                errors.push(`Number of files selected exceeds the maximum of ${max}`);
            }
        }
        return { errors, warnings, options };
    }, [
        errorMap,
        warningMap,
        optionsMap,
        required,
        formDataMapping.accessors,
        label,
        functionDefinitions,
        prevErr,
        prevWarn,
        prevOptions,
        firstErrorMap,
        firstWarningMap,
        firstOptionsMap,
        previousState,
        previousValue,
        warningRefs,
        errorRefs,
        optionRefs,
        min,
        max,
        type
    ]);
    if (!warningMap && !errorMap && !optionsMap && !required) {
        return render(undefined); //<Component {...props} />;
    }
    else {
        return render(validationFn);
    }
};
exports.default = ValidationRender;
